import { useMemo } from 'react'
import { useQuery } from 'react-query'

import type { ProcessedParts } from 'features/ask-yogi/message-rendering'
import { processMessageContent } from 'features/ask-yogi/message-rendering'
import type { AgentInsightService } from 'features/trend-detection/features/insight-agent/service'
import type { AgentInsightDetail } from 'features/trend-detection/features/insight-agent/types'
import type { TableFilterData } from 'src/types'

export interface UseAgentInsightDetailResult {
  insight: AgentInsightDetail | undefined
  processedParts: ProcessedParts | null
  /** recordId -> filter hash, in the shape `MessageContent` expects. */
  citationFilters: { [recordId: string]: string | null }
  /** resultId -> filter data, consumed by `TableFilterModal`. */
  tableFilters: { [resultId: string]: TableFilterData }
  isLoading: boolean
  isError: boolean
}

/**
 * Load one insight and the provenance behind its report body.
 *
 * The report is an Ask-Yogi-shaped message, so the body runs through the shared
 * `processMessageContent`, and the citation / table ids it finds drive two
 * provenance requests. Chart artifacts need no lookup — they are resolved
 * server-side at compile time and travel with the insight.
 *
 * Takes the service rather than reaching for the API client, so the container
 * stays the only consumer of the service hook.
 */
export function useAgentInsightDetail(
  service: AgentInsightService,
  projectId: string | null,
  insightUuid: string | undefined,
): UseAgentInsightDetailResult {
  const enabled = Boolean(projectId && insightUuid)

  const {
    data: insight,
    isLoading,
    isError,
  } = useQuery<AgentInsightDetail>({
    queryKey: ['agent-insight', projectId, insightUuid],
    queryFn: () => service.getInsight(projectId!, insightUuid!),
    enabled,
    refetchOnWindowFocus: false,
  })

  const processedParts = useMemo(
    () => (insight ? processMessageContent(insight.content) : null),
    [insight],
  )

  const recordIds = useMemo(
    () => processedParts?.citationIds ?? [],
    [processedParts],
  )

  // A `<table results="…">` tag may list several ids for one rendered table.
  const resultIds = useMemo(
    () =>
      Array.from(
        new Set((processedParts?.tables ?? []).flatMap((t) => t.resultIds)),
      ),
    [processedParts],
  )

  const { data: citations } = useQuery({
    queryKey: ['agent-insight-citations', projectId, insightUuid, recordIds],
    queryFn: () =>
      service.getCitationFilters(projectId!, insightUuid!, recordIds),
    enabled: enabled && recordIds.length > 0,
    refetchOnWindowFocus: false,
  })

  const { data: tables } = useQuery({
    queryKey: ['agent-insight-tables', projectId, insightUuid, resultIds],
    queryFn: () => service.getTableFilters(projectId!, insightUuid!, resultIds),
    enabled: enabled && resultIds.length > 0,
    refetchOnWindowFocus: false,
  })

  const citationFilters = useMemo(() => {
    const map: { [recordId: string]: string | null } = {}
    citations?.citations.forEach((citation) => {
      map[citation.record_id.toString()] = citation.filter_hash
    })
    return map
  }, [citations])

  const tableFilters = useMemo(() => {
    const map: { [resultId: string]: TableFilterData } = {}
    tables?.table_filters.forEach((entry) => {
      map[entry.result_id] = entry
    })
    return map
  }, [tables])

  return {
    insight,
    processedParts,
    citationFilters,
    tableFilters,
    isLoading,
    isError,
  }
}
